/**
 * Icones em SVG.
 *
 * Antes isto era emoji (📊 ⧉ ⛶). Emoji renderiza diferente em cada sistema,
 * nao herda a cor do tema e destoa do resto da interface — e o jeito mais
 * rapido de uma tela parecer improvisada. Estes seguem o mesmo tracado dos
 * icones do Discord: 24x24, traco de 2, sempre em currentColor.
 */

type Props = { size?: number; className?: string };

const base = (size: number) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
});

/** Monitor com seta subindo — comecar a transmitir. */
export function ScreenShare({ size = 18, className }: Props) {
  return (
    <svg {...base(size)} className={className}>
      <rect x="2" y="4" width="20" height="13" rx="2" />
      <path d="M8 21h8M12 17v4" />
      <path d="M12 13V8m0 0-2.2 2.2M12 8l2.2 2.2" />
    </svg>
  );
}

/** Monitor cortado — parar de transmitir. */
export function ScreenOff({ size = 18, className }: Props) {
  return (
    <svg {...base(size)} className={className}>
      <rect x="2" y="4" width="20" height="13" rx="2" />
      <path d="M8 21h8M12 17v4" />
      <path d="m9.5 8.5 5 5m0-5-5 5" />
    </svg>
  );
}

/** Barras — estatisticas da transmissao. */
export function Chart({ size = 18, className }: Props) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M6 20v-6M12 20V8M18 20v-9" />
    </svg>
  );
}

/** Janela dentro da janela — picture-in-picture. */
export function Pip({ size = 18, className }: Props) {
  return (
    <svg {...base(size)} className={className}>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <rect x="12" y="12" width="7" height="5" rx="1" />
    </svg>
  );
}

/** Setas para fora — tela cheia. */
export function Expand({ size = 18, className }: Props) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3" />
    </svg>
  );
}

/** Um quadro grande — destacar esta tela. */
export function Focus({ size = 18, className }: Props) {
  return (
    <svg {...base(size)} className={className}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M8 9h8v6H8z" />
    </svg>
  );
}

/** Quatro quadros — voltar para a grade. */
export function Grid({ size = 18, className }: Props) {
  return (
    <svg {...base(size)} className={className}>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

/** Alto-falante — cabecalho, indicando o canal de voz de origem. */
export function Voice({ size = 18, className }: Props) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M11 5 6 9H3v6h3l5 4V5Z" />
      <path d="M15.5 8.5a5 5 0 0 1 0 7" />
      <path d="M18.5 5.5a9 9 0 0 1 0 13" />
    </svg>
  );
}

/** Marca do Discord, para o botao de login. Preenchida, nao tracada. */
export function DiscordMark({ size = 20, className }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 127.14 96.36"
      fill="currentColor"
      aria-hidden
      className={className}
    >
      <path d="M107.7 8.07A105.15 105.15 0 0 0 81.47 0a72.06 72.06 0 0 0-3.36 6.83 97.68 97.68 0 0 0-29.11 0A72.37 72.37 0 0 0 45.64 0a105.89 105.89 0 0 0-26.25 8.09C2.79 32.65-1.71 56.6.54 80.21a105.73 105.73 0 0 0 32.17 16.15 77.7 77.7 0 0 0 6.89-11.11 68.42 68.42 0 0 1-10.85-5.18c.91-.66 1.8-1.34 2.66-2a75.57 75.57 0 0 0 64.32 0c.87.71 1.76 1.39 2.66 2a68.68 68.68 0 0 1-10.87 5.19 77 77 0 0 0 6.89 11.1 105.25 105.25 0 0 0 32.19-16.14c2.64-27.38-4.51-51.11-18.9-72.15ZM42.45 65.69C36.18 65.69 31 60 31 53s5-12.74 11.43-12.74S54 46 53.89 53s-5.05 12.69-11.44 12.69Zm42.24 0C78.41 65.69 73.25 60 73.25 53s5-12.74 11.44-12.74S96.23 46 96.12 53s-5.04 12.69-11.43 12.69Z" />
    </svg>
  );
}
